/**
 * Page-access policy: whether a page can open, and whether navigation shows
 * it, from the plan and session reads (`@/lib/access`) and the page's own
 * requirements (`./routes`). The route gate, the section tabs and the Sidebar
 * all read pages through `usePageAccess`, so they cannot disagree about which
 * reads a page needs, who it refuses, or whether a retry is offered.
 */

import type { Permission } from '@/api/types'
import { useEntitlements, usePermissions, type GateRead, type ModuleAccess } from '@/lib/access'

import type { PageDef, SectionDef, TabDef } from './routes'

/** Why a page's access could not be confirmed, and what the gate offers for it. */
export interface AccessFailure {
  retrying: boolean
  retryFailed: boolean
  /** Asks every failed read again; absent when one is a refusal, which asking again cannot change. */
  retry?: () => void
}

/**
 * Where the gate lands for a page, in its order: waiting on a read it needs;
 * refused by the caller's role, or by a plan known to be off — a known refusal
 * wins over anything that could not be read; unknown because a read it needs
 * failed; or open. Navigation hides a `forbidden` page and locks a `disabled`
 * one.
 */
export type PageState = 'pending' | 'forbidden' | 'disabled' | 'unknown' | 'open'

/** How the gate sees a page. Only `unknown` carries a failure, and it always does. */
export type PageAccess = { state: 'unknown'; failure: AccessFailure } | { state: Exclude<PageState, 'unknown'>; failure: null }

/** The gate's title for a page whose access is `unknown`; the Sidebar's hint says the same. */
export const ACCESS_UNKNOWN_TITLE = 'Access could not be confirmed'

/** The reads a page's access depends on: the plan, and the caller's session and the permissions it grants. */
export interface AccessReads {
  plan: GateRead & { moduleAccess: (key: SectionDef['entitlement']) => ModuleAccess }
  session: GateRead & { can: (permission: Permission | null | undefined) => boolean }
}

/** What the gate offers for the reads that failed, or `null` when none did. */
function failureOf(failed: readonly GateRead[]): AccessFailure | null {
  if (failed.length === 0) return null
  return {
    retrying: failed.some((read) => read.isRetrying),
    retryFailed: failed.some((read) => read.retryFailed),
    retry: failed.every((read) => read.retryable) ? () => failed.forEach((read) => void read.refetch()) : undefined,
  }
}

/**
 * A page — its section, and the tab or page definition being opened — as the
 * gate sees it. A read counts against the page only when the page needs it:
 * the plan when its module is `unknown`, the session when a permission is
 * named. The role is judged as soon as the session answers, without waiting on
 * the plan. A retry is offered only when every failed read may pass.
 */
export function decidePageAccess(section: SectionDef, page: Pick<PageDef, 'permission'> | undefined, reads: AccessReads): PageAccess {
  const plan = reads.plan.moduleAccess(section.entitlement)
  // Its section or the page itself names a permission, so the session must be read first.
  const needsRole = Boolean(section.permission || page?.permission)

  if (needsRole && reads.session.isPending) return { state: 'pending', failure: null }
  // Only a session that answered can refuse: one that failed knows no role, so hides nothing.
  const { session } = reads
  if (!session.isFailed && !(session.can(section.permission) && session.can(page?.permission))) {
    return { state: 'forbidden', failure: null }
  }
  if (plan === 'pending') return { state: 'pending', failure: null }
  if (plan === 'disabled') return { state: 'disabled', failure: null }

  const failed: GateRead[] = []
  if (plan === 'unknown') failed.push(reads.plan)
  if (needsRole && reads.session.isFailed) failed.push(reads.session)
  const failure = failureOf(failed)
  if (failure) return { state: 'unknown', failure }
  return { state: 'open', failure: null }
}

/** The section's tabs the caller's role does not refuse — the one rule navigation and the section tabs share. */
export function tabsOpenTo(section: SectionDef, reads: AccessReads): readonly TabDef[] {
  return section.tabs.filter((tab) => decidePageAccess(section, tab, reads).state !== 'forbidden')
}

/** A section as navigation shows it: the tabs it offers, the page it opens on, and where the gate lands there. */
export interface SidebarSection {
  section: SectionDef
  tabs: readonly TabDef[]
  homePage: TabDef | PageDef | undefined
  /** A `forbidden` section is not shown at all. */
  state: Exclude<PageState, 'forbidden'>
  /** The page offers Try again, so navigation may promise it. */
  offersRetry: boolean
}

/**
 * The sections navigation shows, in order. A section with tabs shows while
 * `tabsOpenTo` leaves one, and opens on the first; one the role refuses on
 * that page is hidden.
 */
export function sidebarSections(sections: readonly SectionDef[], reads: AccessReads): SidebarSection[] {
  return sections.flatMap((section) => {
    const tabs = tabsOpenTo(section, reads)
    if (section.tabs.length > 0 && tabs.length === 0) return []
    const homePage = tabs[0] ?? section.pages[0]
    const access = decidePageAccess(section, homePage, reads)
    if (access.state === 'forbidden') return []
    return [{ section, tabs, homePage, state: access.state, offersRetry: access.state === 'unknown' && access.failure.retry !== undefined }]
  })
}

/** `decidePageAccess`, `tabsOpenTo` and `sidebarSections` over this caller's plan and session reads. */
export function usePageAccess() {
  const reads = { plan: useEntitlements(), session: usePermissions() }
  return {
    session: reads.session,
    pageAccess: (section: SectionDef, page?: Pick<PageDef, 'permission'>) => decidePageAccess(section, page, reads),
    visibleTabs: (section: SectionDef) => tabsOpenTo(section, reads),
    navSections: (sections: readonly SectionDef[]) => sidebarSections(sections, reads),
  }
}
